import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";
import { useSessionSecurity } from "@/hooks/useSessionSecurity";
import SessionTimeoutWarning from "./SessionTimeoutWarning";

interface AuthGuardProps {
  children: React.ReactNode;
}

// Dev bypass: DISABLED — auth guards must always be active to prevent
// accidental exposure. Configure VITE_SUPABASE_URL in .env.local for dev.
const DEV_BYPASS = false;

export default function AuthGuard({ children }: AuthGuardProps) {
  const { t } = useTranslation("common");
  const [checking, setChecking] = useState(!DEV_BYPASS);
  const [authenticated, setAuthenticated] = useState(DEV_BYPASS);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (DEV_BYPASS) return;

    const checkAuth = async () => {
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      try {
        // Use getUser() instead of getSession() — getSession() reads from
        // local storage without server verification and can be forged.
        // getUser() validates the JWT against Supabase's auth server.
        // Fail closed: if auth check takes >3s, redirect to login.
        const result = await Promise.race([
          supabase.auth.getUser(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Auth timeout")), 3_000),
          ),
        ]);

        if (result.data.user && !result.error) {
          setAuthenticated(true);
        } else {
          navigate(`/connexion?returnUrl=${returnUrl}&reason=login_required`, { replace: true });
        }
      } catch {
        navigate(`/connexion?returnUrl=${returnUrl}&reason=login_required`, { replace: true });
      } finally {
        setChecking(false);
      }
    };

    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setAuthenticated(false);
        const returnUrl = encodeURIComponent(location.pathname + location.search);
        navigate(`/connexion?returnUrl=${returnUrl}&reason=login_required`, { replace: true });
      } else {
        setAuthenticated(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location]);

  // --- Session security (hooks must be called unconditionally) ---
  const handleSessionLogout = useCallback(async () => {
    await supabase.auth.signOut();
    navigate("/connexion?reason=timeout", { replace: true });
  }, [navigate]);

  const handleValidateSession = useCallback(async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    return !!user && !error;
  }, []);

  const { isWarningVisible, remainingSeconds, extendSession, forceLogout } =
    useSessionSecurity({
      idleTimeoutMs: 30 * 60 * 1000, // 30 min
      absoluteTimeoutMs: 24 * 60 * 60 * 1000, // 24h
      warningBeforeMs: 2 * 60 * 1000, // 2 min warning
      onLogout: handleSessionLogout,
      validateSession: handleValidateSession,
      enabled: authenticated,
    });

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-secondary" />
          <p className="text-sm text-muted-foreground">{t("common.loading", "Chargement...")}</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return null;
  }

  return (
    <>
      {children}
      <SessionTimeoutWarning
        open={isWarningVisible}
        remainingSeconds={remainingSeconds}
        onExtend={extendSession}
        onLogout={forceLogout}
      />
    </>
  );
}
